//system
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpService } from './common/http.service';

//services
import { LogService } from './common/log.service';

//models
import { Location, UtcTimeModel } from 'src/app/models/common-models';
import { EnvironmentService } from './environment.service';
import { MemberLocationSaveRequest } from '../models/requests';
import { Observable } from 'rxjs';
import { Member } from '../models/member';

export interface CorporateWellnessRequestPayload {
  company: string;
  name: string;
  email: string;
  phone?: string;
  companySize?: string;
  interests?: string;
  message?: string;
}

export interface FundraiserRequestPayload {
  organizer: string;
  organization: string;
  email: string;
  phone: string;
  preferredDate1: string;
  preferredDate2?: string;
  nonprofit: string;
  details?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FacilityService {
  private httpServiceInstance: HttpService;
  utcTime!: UtcTimeModel;

  constructor(
    private logService: LogService,
    private httpService: HttpService,
    private http: HttpClient,
    private _environmentService: EnvironmentService
  ) {
    this.httpServiceInstance = this.httpService.createInstance({
      baseUrl: `${this._environmentService.getFacilityConfig().facilityUrl}api/v${this._environmentService.getFacilityConfig().facilityApiVersion}`
    });
  }

  async getUtcDateTime(): Promise<UtcTimeModel> {
    return await this.httpServiceInstance.getPromise<UtcTimeModel>(`Facility/Time`);
  }

  async isClockSynced(): Promise<boolean> {
    this.utcTime = await this.getUtcDateTime();
    this.utcTime.UtcTime = new Date(this.utcTime.UtcTime);

    const browserTimestamp = new Date();
    const timeDifference = Math.abs(browserTimestamp.getTime() - this.utcTime.UtcTime.getTime());
    const threshold = 5 * 60 * 1000;
    return timeDifference < threshold;
  }

  private locationsPromise: Promise<Location[]> | null = null;

  getLocations(): Promise<Location[]> {
    if (!this.locationsPromise) {
      this.locationsPromise = this.httpServiceInstance
        .getPromise<Location[]>(`Location/WithDetails`)
        .catch((err) => {
          this.locationsPromise = null;
          throw err;
        });
    }
    return this.locationsPromise;
  }

  getLocationById(locationId: number): Promise<Location> {
    return this.httpServiceInstance.getPromise<Location>(`Location/Details/${locationId}`);
  }

  changeLocation(request: MemberLocationSaveRequest): Observable<Member> {
    return this.httpService.post<Member>(`Member/ChangeLocation`, request);
  }

  /**
   * Corporate wellness form — dedicated endpoint (not Contact Us / SupportRequest).
   * FormData + X-Skip-Interceptor matches the public Contact Us transport style.
   * Captcha is UI-only (not appended) — same as SupportRequest.
   */
  submitCorporateWellnessRequest(payload: CorporateWellnessRequestPayload): Observable<boolean> {
    const formData = new FormData();
    formData.append('Company', payload.company ?? '');
    formData.append('Name', payload.name ?? '');
    formData.append('Email', payload.email ?? '');
    formData.append('Phone', payload.phone ?? '');
    formData.append('CompanySize', payload.companySize ?? '');
    formData.append('Interests', payload.interests ?? '');
    formData.append('Message', payload.message ?? '');
    return this.postPublicFacilityForm('Facility/CorporateWellnessRequest', formData);
  }

  /**
   * Fundraisers form — dedicated endpoint (not Contact Us / SupportRequest).
   * Captcha is UI-only (not appended) — same as SupportRequest.
   */
  submitFundraiserRequest(payload: FundraiserRequestPayload): Observable<boolean> {
    const formData = new FormData();
    formData.append('Organizer', payload.organizer ?? '');
    formData.append('Organization', payload.organization ?? '');
    formData.append('Email', payload.email ?? '');
    formData.append('Phone', payload.phone ?? '');
    formData.append('PreferredDate1', payload.preferredDate1 ?? '');
    formData.append('PreferredDate2', payload.preferredDate2 ?? '');
    formData.append('Nonprofit', payload.nonprofit ?? '');
    formData.append('Details', payload.details ?? '');
    return this.postPublicFacilityForm('Facility/FundraiserRequest', formData);
  }

  private postPublicFacilityForm(path: string, formData: FormData): Observable<boolean> {
    const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');
    const config = this._environmentService.getFacilityConfig();
    const url = `${config.facilityUrl}api/v${config.facilityApiVersion}/${path}`;
    return this.http.post<boolean>(url, formData, { headers });
  }

  isDF(): boolean {
    return this._environmentService.getFacilityConfig().submerchant === 'dropfitness';
  }

  isFF(): boolean {
    return this._environmentService.getFacilityConfig().submerchant === 'fitnessfactory';
  }

  isLH(): boolean {
    return this._environmentService.getFacilityConfig().submerchant === 'luckyhoney';
  }

  isSH(): boolean {
    return this._environmentService.getFacilityConfig().submerchant === 'steelhaus';
  }

  /** DFS-2020: facility JSON `allowCrossMembershipUpgrade` (no Clients getAppAssets on website). */
  isCrossMembershipUpgradeAllowed(): boolean {
    return this._environmentService.getFacilityConfig().allowCrossMembershipUpgrade === true;
  }
}
